import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Settings as SettingsIcon, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import CreatePostDialog from '../components/CreatePostDialog';
import PostActions from '../components/PostActions';
import PostAttachment from '../components/PostAttachment';
import ChannelList from '../components/ChannelList';
import ChannelSettingsDialog from '../components/ChannelSettingsDialog';

interface Channel {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  cover_image: string | null;
  creator_id: string;
  is_member: boolean;
  role: 'owner' | 'moderator' | 'member' | null;
}

interface Post {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  channel?: {
    id: string;
    name: string;
  } | null;
  attachments: Array<{
    id: string;
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnail_url?: string;
  }>;
  likes: number;
  comments: number;
  liked_by_me: boolean;
}

function ChannelPage() {
  const { channelName } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (channelName) {
      fetchChannel();
    }
  }, [channelName]);

  useEffect(() => {
    if (channel?.id) {
      fetchPosts();
    }
  }, [channel?.id]);

  const fetchChannel = async () => {
    if (!channelName || !user) return;

    try {
      setError(null);
      
      // Special handling for explore page
      if (channelName === 'explore') {
        setChannel(null);
        setLoading(false);
        return;
      }

      // Get channel info and check membership
      const { data: channelData, error: channelError } = await supabase
        .from('channels')
        .select(`
          id,
          name,
          description,
          member_count,
          cover_image,
          creator_id
        `)
        .eq('name', channelName.toLowerCase())
        .single();

      if (channelError) throw channelError;

      if (!channelData) {
        setError('Channel not found');
        setLoading(false);
        return;
      }

      // Check if user is a member
      const { data: membership, error: membershipError } = await supabase
        .from('channel_members')
        .select('role')
        .eq('channel_id', channelData.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipError && membershipError.code !== 'PGRST116') {
        throw membershipError;
      }
      
      setChannel({
        id: channelData.id,
        name: channelData.name,
        description: channelData.description,
        member_count: channelData.member_count,
        cover_image: channelData.cover_image,
        creator_id: channelData.creator_id,
        is_member: !!membership,
        role: membership?.role || null
      });
    } catch (error) {
      console.error('Error fetching channel:', error);
      setError('Failed to load channel');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    if (!channel?.id) return;

    try {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          user:users(id, username, display_name, avatar_url),
          attachments:post_attachments(
            id,
            type,
            url,
            title,
            description,
            thumbnail_url
          ),
          likes:likes(count),
          comments:comments(count)
        `)
        .eq('channel_id', channel.id)
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      if (user) {
        const { data: userLikes } = await supabase
          .from('likes')
          .select('post_id')
          .eq('user_id', user.id);

        const likedPostIds = new Set(userLikes?.map(like => like.post_id) || []);

        const formattedPosts = postsData?.map(post => ({
          ...post,
          attachments: post.attachments || [],
          likes: post.likes[0]?.count ?? 0,
          comments: post.comments[0]?.count ?? 0,
          liked_by_me: likedPostIds.has(post.id)
        })) || [];

        setPosts(formattedPosts);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to load posts');
    }
  };

  const handleJoinChannel = async () => {
    if (!channel || !user || joining) return;

    setJoining(true);
    try {
      const { error } = await supabase
        .from('channel_members')
        .insert({
          channel_id: channel.id,
          user_id: user.id,
          role: 'member'
        });

      if (error) throw error;
      await fetchChannel();
    } catch (error) {
      console.error('Error joining channel:', error);
      alert('Failed to join channel');
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveChannel = async () => {
    if (!channel || !user || leaving || channel.role === 'owner') return;

    setLeaving(true);
    try {
      const { error } = await supabase
        .from('channel_members')
        .delete()
        .eq('channel_id', channel.id)
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchChannel();
    } catch (error) {
      console.error('Error leaving channel:', error);
      alert('Failed to leave channel');
    } finally {
      setLeaving(false);
    }
  };

  const handleCreatePost = async (content: string) => {
    if (!channel || !user) return;

    try {
      const { error: postError } = await supabase
        .from('posts')
        .insert({
          content: content.trim(),
          user_id: user.id,
          channel_id: channel.id
        });

      if (postError) throw postError;
      await fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto pt-4">
        <Link
          to="/"
          className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  // Show channel list for explore page
  if (channelName === 'explore') {
    return (
      <div className="max-w-2xl mx-auto space-y-6 pt-4">
        <Link
          to="/"
          className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold mb-6">Explore Channels</h1>
          <ChannelList />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 pt-4">
        <Link
          to="/"
          className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link
            to="/channel/explore"
            className="text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            Explore other channels
          </Link>
        </div>
      </div>
    );
  }

  if (!channel) return null;

  const isOwner = user?.id === channel.creator_id;

  return (
    <div className="max-w-2xl mx-auto space-y-6 pt-4">
      <Link
        to="/"
        className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Home
      </Link>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {channel.cover_image && (
          <div className="h-48 bg-gray-100">
            <img
              src={channel.cover_image}
              alt={channel.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">#{channel.name}</h1>
              </div>
              {channel.description && (
                <p className="text-gray-600 mt-2">{channel.description}</p>
              )}
              <div className="flex items-center text-gray-500 mt-4">
                <Users className="h-5 w-5 mr-2" />
                {channel.member_count} members
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!channel.is_member && (
                <button
                  onClick={handleJoinChannel}
                  disabled={joining}
                  className={`px-4 py-2 bg-indigo-600 text-white rounded-lg transition-colors ${
                    joining ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
                  }`}
                >
                  {joining ? 'Joining...' : 'Join Channel'}
                </button>
              )}
              {channel.is_member && !isOwner && (
                <button
                  onClick={handleLeaveChannel}
                  disabled={leaving}
                  className={`px-4 py-2 bg-red-600 text-white rounded-lg transition-colors ${
                    leaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'
                  }`}
                >
                  {leaving ? 'Leaving...' : 'Leave Channel'}
                </button>
              )}
              {isOwner && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                  title="Channel Settings"
                >
                  <SettingsIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {channel.is_member && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <button
            onClick={() => setShowCreatePost(true)}
            className="w-full px-4 py-2 bg-gray-100 rounded-lg text-gray-500 text-left hover:bg-gray-200 transition-colors"
          >
            What's on your musical mind?
          </button>
        </div>
      )}

      <div className="space-y-6">
        {posts.map(post => (
          <div key={post.id} className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-start space-x-4">
              <Link to={`/profile/${post.user.id}`}>
                <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                  {post.user.avatar_url ? (
                    <img 
                      src={post.user.avatar_url} 
                      alt={post.user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                </div>
              </Link>
              <div className="flex-1">
                <Link 
                  to={`/profile/${post.user.id}`}
                  className="font-semibold hover:text-indigo-600 transition-colors"
                >
                  {post.user.display_name || post.user.username}
                </Link>
                <p className="text-sm text-gray-500">
                  {new Date(post.created_at).toLocaleString()}
                </p>
                
                <p className="text-gray-900 mt-2">{post.content}</p>

                {post.attachments?.map((attachment) => (
                  <PostAttachment
                    key={attachment.id}
                    type={attachment.type}
                    url={attachment.url}
                    title={attachment.title}
                    description={attachment.description}
                    thumbnailUrl={attachment.thumbnail_url}
                  />
                ))}

                <PostActions
                  postId={post.id}
                  userId={post.user.id}
                  likes={post.likes}
                  comments={post.comments}
                  liked={post.liked_by_me}
                  onLikeChange={fetchPosts}
                  onDelete={fetchPosts}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <CreatePostDialog
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onSubmit={handleCreatePost}
      />

      {channel && isOwner && (
        <ChannelSettingsDialog
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          channelId={channel.id}
          initialData={{
            name: channel.name,
            description: channel.description,
            cover_image: channel.cover_image
          }}
          onSuccess={fetchChannel}
        />
      )}
    </div>
  );
}

export default ChannelPage;